import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Auth } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';
import routesfe from '../Config/RoutesFE';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { HiUserAdd } from 'react-icons/hi';
import SignUpVerifyEmail from './SignUpVerifyEmail';
import SignUpRegistrationInitiated from './SignupRegistrationInitiated';
import configDetails from '../Config/Config';
import Toast from '../components copy/Toast';
import logger from '../utils/Logger';

const COMPONENT_NAME = 'SignupPage';

// Zod Schema for Validation
const signupSchema = z
  .object({
    name: z.string().min(1, 'Full name is required'),
    email: z.string().email('Invalid email format'),
    phoneNumber: z
      .string()
      .regex(/^(\(\d{3}\) \d{3}-\d{4})$/, 'Invalid phone number.'),
    countryCode: z.string().min(1, 'Country code is required'),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/,  
        'Password must include one uppercase, one lowercase, one number, and one special character'
      ),
    confirmPassword: z.string().min(1, 'Confirm password is required'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

function SignupPage() {
  const [stage, setStage] = useState('signup');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [toast, setToast] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordHelp, setShowPasswordHelp] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      name: '',
      email: '',
      phoneNumber: '',
      countryCode: '+1',
      password: '',
      confirmPassword: '',
    },
  });

  // Toggle password visibility
  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

  // Format phone number (123) 456-7890
  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    return match ? `(${match[1]}) ${match[2]}-${match[3]}` : value;
  };

  // Handle user signup
  const handleSignup = async (data) => {
    setLoading(true);
    
    logger.info('User signup attempt started', {
      component: COMPONENT_NAME,
      action: 'SIGNUP_ATTEMPT',
      context: {
        email: data.email,
        name: data.name,
        timestamp: Date.now()
      }
    });
    
    try {
      const attributes = {
        email: data.email,
        name: data.name,
        phone_number: `${data.countryCode}${data.phoneNumber.replace(/\D/g, '')}`,
        'custom:is_subscribed': "0",
        'custom:is_trial_user': "0"
      };

      await Auth.signUp({
        username: data.email,
        password: data.password,
        attributes,
      });
      
      logger.info('User signup successful', {
        component: COMPONENT_NAME,
        action: 'SIGNUP_SUCCESS',
        context: {
          email: data.email,
          timestamp: Date.now()
        }
      });
      
      setStage('verify');
    } catch (error) {
      logger.error('User signup failed', {
        component: COMPONENT_NAME,
        action: 'SIGNUP_FAILED',
        error: error,
        context: {
          email: data.email,
          errorCode: error.code,
          errorName: error.name,
          errorMessage: error.message,
          errorStack: error.stack,
          timestamp: Date.now()
        }
      });
      
      if (error.code === 'UsernameExistsException') {
        setErrorMessage('User already exists. Please log in or use a different email address.');
      } else {
        setErrorMessage('An error occurred during signup. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  //Handle confirmation of signup
  const handleConfirmSignup = async (data) => {
    setLoading(true);
    
    logger.info('Signup confirmation attempt started', {
      component: COMPONENT_NAME,
      action: 'CONFIRM_SIGNUP_ATTEMPT',
      context: {
        email: data.email,
        timestamp: Date.now()
      }
    });
    
    try {
      await Auth.confirmSignUp(data.email, data.verificationCode);
      
      logger.info('Signup confirmation successful', {
        component: COMPONENT_NAME,
        action: 'CONFIRM_SIGNUP_SUCCESS',
        context: {
          email: data.email,
          timestamp: Date.now()
        }
      });
      
      setToast({ message: 'Signup confirmed! You can now log in. 🎉', type: 'success' });
      setTimeout(() => {
        navigate(routesfe.LOGIN);
      }, 1500);
    } catch (error) {
      logger.error('Signup confirmation failed', {
        component: COMPONENT_NAME,
        action: 'CONFIRM_SIGNUP_FAILED',
        error: error,
        context: {
          email: data.email,
          errorCode: error.code,
          errorName: error.name,
          errorMessage: error.message,
          errorStack: error.stack,
          timestamp: Date.now()
        }
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Toast toast={toast} onClose={() => setToast(null)} />
      <div className="w-full min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-100 via-blue-50 to-teal-50 py-10">
      <div
        className="relative w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl
        bg-white border border-gray-200 shadow-2xl
        rounded-xl p-8 sm:p-10 md:p-12 flex flex-col"
        // For sharper rectangle corners, use `rounded-md` or remove rounded-* completely
      >
        {/* Logo and title */}
        <div className="flex flex-col items-center gap-1 mb-6">
          <HiUserAdd className="text-indigo-500 text-4xl drop-shadow-md" />
          <span className="text-2xl font-extrabold tracking-tight text-indigo-700">Sign Up</span>
        </div>

        <h2 className="text-xl md:text-2xl font-bold text-center text-gray-900 mb-5 tracking-tight drop-shadow-sm">
          {stage === 'signup' ? 'Welcome to NOTEFORGE' : 'Verify Your Account'}
        </h2>

        {stage === 'signup' ? (
          <form onSubmit={handleSubmit(handleSignup)} autoComplete="off" className="space-y-5">
            {/* Error Message */}
            {errorMessage && (
              <div className="mt-2 text-center rounded bg-red-50 border border-red-200 text-red-600 font-medium text-sm py-2 px-3 mb-2">
                {errorMessage}
              </div>
            )}

              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      autoFocus
                      type="text"
                      placeholder="Enter your full name"
                      className={`block w-full px-4 py-2 text-base border ${
                        errors.name ? 'border-red-400' : 'border-gray-300'
                      } rounded-lg outline-none focus:ring-2 focus:ring-indigo-400 bg-white transition`}
                    />
                  )}
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>
                )}
              </div>

              {/* Email and Phone Number */}
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="email"
                        placeholder="Enter your email"
                        className={`block w-full px-4 py-2 text-base border ${
                          errors.email ? 'border-red-400' : 'border-gray-300'
                        } rounded-lg outline-none focus:ring-2 focus:ring-indigo-400 bg-white transition`}
                        autoComplete="username"
                      />
                    )}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <div className="flex gap-2">
                    <Controller
                      name="countryCode"
                      control={control}
                      render={({ field }) => (
                        <select
                          {...field}
                          className="w-1/3 text-base px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 bg-white transition"
                        >
                          <option value="+1">🇺🇸 +1</option>
                          <option value="+44">🇬🇧 +44</option>
                          <option value="+91">🇮🇳 +91</option>
                        </select>
                      )}
                    />
                    <Controller
                      name="phoneNumber"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="tel"
                          placeholder="(123) 456-7890"
                          onChange={e => field.onChange(formatPhoneNumber(e.target.value))}
                          className={`w-2/3 px-4 py-2 text-base border ${
                            errors.phoneNumber ? 'border-red-400' : 'border-gray-300'
                          } rounded-lg outline-none focus:ring-2 focus:ring-indigo-400 bg-white transition`}
                        />
                      )}
                    />
                  </div>
                  {errors.phoneNumber && (
                    <p className="text-red-500 text-xs mt-1">{errors.phoneNumber.message}</p>
                  )}
                </div>
              </div>

              {/* Password and Confirm Password */}
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <div className="relative">
                    <Controller
                      name="password"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          type={showPassword ? "text" : "password"}
                          autoComplete="new-password"
                          placeholder="Password"
                          className={`w-full px-4 py-2 text-base border ${
                            errors.password ? 'border-red-400' : 'border-gray-300'
                          } rounded-lg outline-none focus:ring-2 focus:ring-indigo-400 bg-white transition`}
                        />
                      )}
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={togglePasswordVisibility}
                      className="absolute inset-y-0 right-0 px-3 flex items-center text-lg text-gray-400 hover:text-indigo-500 focus:outline-none"
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowPasswordHelp(h => !h)}
                    className="text-xs text-indigo-400 hover:text-indigo-600 underline focus:outline-none mt-1"
                  >
                    Show password requirements
                  </button>
                  {showPasswordHelp && (
                    <ul className="text-[13px] text-left text-indigo-500 bg-indigo-50 p-2 rounded mt-1 border border-indigo-200">
                      <li>• At least 8 characters</li>
                      <li>• One uppercase letter</li>
                      <li>• One lowercase letter</li>
                      <li>• One number</li>
                      <li>• One special character</li>
                    </ul>
                  )}
                  {errors.password && (
                    <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                  <div className="relative">
                    <Controller
                      name="confirmPassword"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          type={showConfirmPassword ? "text" : "password"}
                          autoComplete="new-password"
                          placeholder="Re-enter password"
                          className={`w-full px-4 py-2 text-base border ${
                            errors.confirmPassword ? 'border-red-400' : 'border-gray-300'
                          } rounded-lg outline-none focus:ring-2 focus:ring-indigo-400 bg-white transition`}
                        />
                      )}
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={toggleConfirmPasswordVisibility}
                      className="absolute inset-y-0 right-0 px-3 flex items-center text-lg text-gray-400 hover:text-indigo-500 focus:outline-none"
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-red-500 text-xs mt-1">{errors.confirmPassword.message}</p>
                  )}
                </div>
              </div>

              {/* Sign Up Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full flex justify-center gap-2 py-2 px-4 text-base font-semibold rounded-lg shadow-md transition focus:outline-none ${
                    loading 
                      ? "bg-gray-300 cursor-not-allowed text-gray-500"
                      : "bg-gradient-to-r from-indigo-600 via-indigo-500 to-blue-500 text-white hover:from-indigo-500 hover:via-indigo-400 hover:to-blue-400 hover:shadow-lg"
                  }`}
                >
                  {loading ? 'Signing Up...' : 'Sign Up'}
                  {!loading && <HiUserAdd className="inline-block mt-0.5" />}
                </button>
              </div>
            </form>
        ) : configDetails.cognitoUserAutoVerification === 'true' ? (
          <SignUpVerifyEmail email={watch('email')} setStage={setStage} />
        ) : (
          <SignUpRegistrationInitiated email={watch('email')} />
        )}

        {/* Terms and Privacy Policy Agreement */}
        <div className="pt-4 text-center text-xs text-gray-500">
          By using Noteforge you agree to the{' '}
          <button
            type="button"
            onClick={() => navigate(routesfe.TERMS_OF_SERVICE)}
            className="text-indigo-500 hover:text-indigo-700 hover:underline focus:outline-none"
          >
            Terms of Service
          </button>
          {' '}and{' '}
          <button
            type="button"
            onClick={() => navigate(routesfe.PRIVACY_POLICY)}
            className="text-indigo-500 hover:text-indigo-700 hover:underline focus:outline-none"
          >
            Privacy Policy
          </button>
        </div>
        {/* Already have an account? */}
          <div className="pt-4 flex flex-col sm:flex-row justify-between items-center">
          <button
            type="button"
            onClick={() => navigate(routesfe.LANDING)}
            className="text-xs rounded inline-flex items-center px-2 py-1 text-indigo-500 hover:text-indigo-700 bg-gray-100 hover:bg-indigo-50 transition font-medium mb-3 self-start"
          >
            <span className="mr-1 text-base">←</span> Back to Home
          </button>
          <span className="text-gray-500 text-sm">
            Already have an account?{' '}
            <button
              onClick={() => navigate(routesfe.LOGIN)}
              className="text-indigo-500 hover:underline hover:text-indigo-700 focus:outline-none"
              type="button"
            >
              Log in
            </button>
          </span>
        </div>
      </div>
      </div>
    </>
  );
}

export default SignupPage;